import { useMemo, useEffect } from 'react';
import { useGetRfiTypes } from './queries';
import {
  Assignee,
  CustomAttributeValue,
  EntityStatus,
  RfiStatus,
  WorkflowType,
  RfiPriority,
} from '../sdk';
import { useOxygen } from '../providers/OxygenProvider';
import { incrementTodayByDays } from '../helpers';
import { getFilterValidAssignees } from '../helpers/oxygenHelpers';

export type FieldSetters = {
  assignedTo: (assignees: Assignee[]) => void;
  locationDescription?: (locationDescription: string) => void;
  reference?: (reference: string) => void;
  costImpact?: (costImpact: string) => void;
  scheduleImpact?: (scheduleImpact: string) => void;
  priority?: (priority: string) => void;
  category?: (category: string[]) => void;
  discipline?: (discipline: string[]) => void;
  dueDateOffset?: (date: Date | null) => void;
  watchers?: (assignees: Assignee[]) => void;
  coReviewers?: (assignees: Assignee[]) => void;
  customAttributes?: (defaults: CustomAttributeValue[]) => void;
};
type usePopulateRfiTypeFieldsArgs = {
  selectedRfiTypeId: string;
  fieldSetters: FieldSetters;
  formStatus: RfiStatus | undefined;
  disabled?: boolean;
};
export const usePopulateRfiTypeFields = ({
  selectedRfiTypeId,
  fieldSetters,
  formStatus,
  disabled,
}: usePopulateRfiTypeFieldsArgs) => {
  const oxygen = useOxygen();
  const filterValidAssignees = useMemo(() => getFilterValidAssignees(oxygen), [oxygen]);

  const { data: rfiTypes, isLoading: isFetchingRfiTypes } = useGetRfiTypes();

  const selectedRfiType = useMemo(() => {
    const activeTypes = rfiTypes?.filter(
      ({ status, id }) => status === EntityStatus.Active || id === selectedRfiTypeId,
    );

    const rfiType = activeTypes?.find((rfiType) => rfiType.id === selectedRfiTypeId);
    return rfiType ? { ...rfiType, didPopulateFields: false } : undefined;
  }, [rfiTypes, selectedRfiTypeId]);

  const handlePopulatingAssignedTo = useMemo(
    () => () => {
      switch (formStatus) {
        case RfiStatus.Open:
        case RfiStatus.OpenRev2: {
          fieldSetters.assignedTo(filterValidAssignees(selectedRfiType?.projectReviewer));
          break;
        }
        case RfiStatus.OpenRev1: {
          fieldSetters.assignedTo(filterValidAssignees(selectedRfiType?.projectCoordinator));
          break;
        }
        case RfiStatus.Submitted: {
          // AssignedTo - Manager
          if (formStatus === RfiStatus.Submitted) {
            fieldSetters.assignedTo(filterValidAssignees(selectedRfiType?.manager) || []);
          }
          break;
        }
        default:
      }
    },
    [formStatus, fieldSetters, filterValidAssignees, selectedRfiType],
  );

  useEffect(() => {
    const doDoNotPopulateFields =
      !selectedRfiType ||
      isFetchingRfiTypes ||
      oxygen.loading ||
      disabled === true ||
      formStatus === undefined;

    if (doDoNotPopulateFields) {
      return;
    }

    const rfiTypeWorkflowMatchFormStatus =
      ([RfiStatus.Submitted, RfiStatus.Open].includes(formStatus) &&
        selectedRfiType.wfType === WorkflowType.US) ||
      ([RfiStatus.Submitted, RfiStatus.OpenRev1, RfiStatus.OpenRev2].includes(formStatus) &&
        selectedRfiType.wfType === WorkflowType.EU);

    if (!rfiTypeWorkflowMatchFormStatus) {
      return;
    }

    // Populate assignedTo based on status and RFI Type
    handlePopulatingAssignedTo();

    const {
      locationDescription,
      reference,
      costImpact,
      scheduleImpact,
      priority,
      category,
      discipline,
      dueDateOffset,
      watchers,
      coReviewers,
      optionalReviewers,
      didPopulateFields,
    } = selectedRfiType;

    if (didPopulateFields) {
      return;
    }

    // locationDetails
    fieldSetters.locationDescription?.(locationDescription || '');
    // reference (External ID )
    fieldSetters.reference?.(reference || '');
    // costImpact
    fieldSetters.costImpact?.(costImpact || '');
    // scheduleImpact
    fieldSetters.scheduleImpact?.(scheduleImpact || '');
    // priority
    fieldSetters.priority?.(priority || RfiPriority.Normal);
    // category
    fieldSetters.category?.(category || []);
    // discipline
    fieldSetters.discipline?.(discipline || []);
    // dueDateOffset
    if (fieldSetters.dueDateOffset) {
      if (dueDateOffset) {
        fieldSetters.dueDateOffset(incrementTodayByDays(dueDateOffset));
      } else {
        fieldSetters.dueDateOffset(null);
      }
    }
    // watchers
    if (fieldSetters.watchers) {
      fieldSetters.watchers(filterValidAssignees(watchers));
    }
    // coReviewers — when optional-reviewers FF is on, types may only carry optionalReviewers after read normalization
    if (fieldSetters.coReviewers) {
      fieldSetters.coReviewers(filterValidAssignees(optionalReviewers ?? coReviewers));
    }
    // customAttributes
    if (fieldSetters.customAttributes && selectedRfiType.customAttributes?.length) {
      fieldSetters.customAttributes(selectedRfiType.customAttributes);
    }

    selectedRfiType.didPopulateFields = true;
  }, [
    selectedRfiTypeId,
    formStatus,
    isFetchingRfiTypes,
    disabled,
    fieldSetters,
    selectedRfiType,
    oxygen.loading,
    filterValidAssignees,
    handlePopulatingAssignedTo,
  ]);
};
